/**
 * VC Gallery - Pattern-Based Image Gallery System
 * Uses pre-generated layout patterns for optimal performance
 * No runtime calculations - just pattern lookup and rendering
 */

(function() {
  'use strict';

  /**
   * Pattern Cache
   * Caches loaded pattern files to avoid redundant fetches
   */
  class PatternCache {
    constructor(baseUrl = '/assets/') {
      this.cache = new Map();
      this.baseUrl = baseUrl;
    }

    async getPattern(filename) {
      if (this.cache.has(filename)) {
        return this.cache.get(filename);
      }

      try {
        // Use base URL from Liquid (includes proper CDN path for online)
        const assetUrl = `${this.baseUrl}${filename}`;

        const response = await fetch(assetUrl);

        if (!response.ok) {
          throw new Error(`Failed to load pattern file: ${filename} (${response.status})`);
        }

        const data = await response.json();
        this.cache.set(filename, data);
        return data;
      } catch (error) {
        // Silent fail for production, but log in development for debugging
        if (window.location.hostname === 'localhost' || window.location.hostname.includes('.myshopify.com')) {
          console.warn('[VCGallery] Pattern load failed:', filename, error);
        }
        return null;
      }
    }
  }

  /**
   * Device Detector
   * Detects current device type based on viewport width
   */
  class DeviceDetector {
    constructor() {
      // Initialize breakpoints once to avoid recreating MediaQueryList objects
      // Breakpoint at 751px to match CSS media queries (accounts for Chrome DevTools viewport rounding)
      this.breakpoints = {
        mobile: window.matchMedia('(max-width: 499px)'),
        tablet: window.matchMedia('(min-width: 500px) and (max-width: 750px)'),
        desktop: window.matchMedia('(min-width: 751px)')
      };
    }

    getCurrentDevice() {
      const width = window.innerWidth;
      // Match breakpoints exactly with MediaQuery listeners
      if (width >= 751) return 'desktop';
      if (width >= 500) return 'tablet';
      return 'mobile';
    }

    onChange(callback) {
      Object.values(this.breakpoints).forEach(breakpoint => {
        breakpoint.addEventListener('change', () => {
          callback(this.getCurrentDevice());
        });
      });
    }
  }

  /**
   * Pattern Selector
   * Selects the correct pattern file and variant based on context
   */
  class PatternSelector {
    constructor(patternCache) {
      this.patternCache = patternCache;
    }

    /**
     * Get pattern filename based on device and group type
     */
    getPatternFilename(device, isSubGroup) {
      const groupType = isSubGroup ? 'subgroup' : 'group';
      return `${groupType}_${device}.json`;
    }

    /**
     * Load and select pattern for given context
     */
    async selectPattern(itemCount, device, isSubGroup) {
      const filename = this.getPatternFilename(device, isSubGroup);
      const patternData = await this.patternCache.getPattern(filename);

      if (!patternData) {
        return this.getFallbackPattern(itemCount);
      }

      // Get pattern for item count (fallback to max available)
      const countKey = itemCount.toString();
      const patternArray = patternData[countKey] || patternData['20'];

      if (!patternArray || patternArray.length === 0) {
        return this.getFallbackPattern(itemCount);
      }

      // Take the first (and only) pattern
      const pattern = patternArray[0];

      return pattern;
    }

    /**
     * Fallback pattern if loading fails
     */
    getFallbackPattern(itemCount) {
      // Use Array.from to create pattern array efficiently
      return Array.from({ length: itemCount }, () => ({
        size: 'narrow',
        fr: 1,
        height: 'full'
      }));
    }
  }

  /**
   * Pattern Applier
   * Applies pattern to gallery items
   */
  class PatternApplier {
    constructor() {
      // Grid layout constant - 4 visual columns on desktop (16fr system)
      this.ITEMS_PER_ROW = 4;
    }

    /**
     * Apply pattern to items
     * Returns array of enhanced items with pattern properties
     */
    applyPattern(items, pattern) {
      const enhancedItems = [];

      // Pattern is a flat array of slot definitions
      // Each slot has: { size: 'wide', fr: 2, height: 'half' }
      for (let i = 0; i < items.length && i < pattern.length; i++) {
        const patternSlot = pattern[i];
        const item = items[i];

        enhancedItems.push({
          element: item,
          layoutSize: patternSlot.size,
          gridFr: patternSlot.fr,
          layoutHeight: patternSlot.height || 'full', // Default to 'full' if missing
          rowIndex: Math.floor(i / this.ITEMS_PER_ROW), // Approximate row (for lazy loading)
          colIndex: i % this.ITEMS_PER_ROW,
          itemIndex: i
        });
      }

      return enhancedItems;
    }
  }

  /**
   * Item Renderer
   * Renders individual gallery items with correct image and styling
   */
  class ItemRenderer {
    constructor() {
      // Device-specific dimensions (16fr system)
      // Main Group: fullsize (16fr), wide (8fr), narrow (4fr)
      // Subgroup: fullsize (8fr), wide (4fr), narrow (2fr) - 50% scaled
      this.dimensionsByDevice = {
        desktop: {
          group: {
            fullsize: { width1x: 1440, width2x: 2880 },  // 16fr = full width
            wide: { width1x: 715, width2x: 1430 },       // 8fr = half width
            narrow: { width1x: 353, width2x: 706 }       // 4fr = quarter width
          },
          subgroup: {
            fullsize: { width1x: 715, width2x: 1430 },   // 8fr = half width (same as group wide)
            wide: { width1x: 353, width2x: 706 },        // 4fr = quarter width (same as group narrow)
            narrow: { width1x: 176, width2x: 352 }       // 2fr = eighth width (NEW)
          }
        },
        tablet: {
          group: {
            fullsize: { width1x: 750, width2x: 1500 },   // 16fr = full width
            wide: { width1x: 750, width2x: 1500 },       // 16fr = full width
            narrow: { width1x: 375, width2x: 750 }       // 8fr = half width
          },
          subgroup: {
            fullsize: { width1x: 375, width2x: 750 },    // 8fr = half width (same as group narrow)
            wide: { width1x: 187, width2x: 374 },        // 4fr = quarter width
            narrow: { width1x: 93, width2x: 186 }        // 2fr = eighth width
          }
        },
        mobile: {
          group: {
            fullsize: { width1x: 375, width2x: 750 },    // 16fr = full width
            wide: { width1x: 375, width2x: 750 },        // 16fr = full width
            narrow: { width1x: 375, width2x: 750 }       // 16fr = full width
          },
          subgroup: {
            fullsize: { width1x: 187, width2x: 374 },    // 8fr = half width
            wide: { width1x: 187, width2x: 374 },        // 4fr or 8fr
            narrow: { width1x: 93, width2x: 186 }        // 2fr or 4fr
          }
        }
      };

      // Fallback chain based on layout size
      this.fallbackChain = {
        fullsize: ['fullsize', 'wide', 'narrow'],
        wide: ['wide', 'fullsize', 'narrow'],
        narrow: ['narrow', 'wide', 'fullsize']
      };

      // Maximum available sizes for each image type (based on original 2x Retina images)
      // These limits prevent upscaling beyond the original image dimensions
      this.maxImageSizes = {
        fullsize: 2880,  // Original: 2880×960px
        wide: 1430,      // Original: 1430×960px
        narrow: 706      // Original: 706×960px
      };
    }

    /**
     * Render an enhanced item
     */
    render(enhancedItem, device, isSubGroup = false) {
      const { element, layoutSize, gridFr, rowIndex, layoutHeight, itemIndex } = enhancedItem;

      // Clear previous state
      element.classList.remove(
        'vc-gallery-item--fullsize',
        'vc-gallery-item--wide',
        'vc-gallery-item--narrow',
        'vc-gallery-item--height-full',
        'vc-gallery-item--height-half'
      );
      // Serverseitig ausgegebenes Bild (data-ssr, Einzelbild-Galerie) behalten: <picture> deckt alle Geraete ab,
      // kein Neuaufbau -> frueher LCP, kein Layout-Sprung (02.10.2026)
      const keepSsr = element.dataset.ssr === 'true';
      if (!keepSsr) element.innerHTML = '';

      // Add size class
      element.classList.add(`vc-gallery-item--${layoutSize}`);

      // Add height class
      element.classList.add(`vc-gallery-item--height-${layoutHeight}`);

      // Set grid-column span directly based on fr value (for 16fr system)
      element.style.gridColumn = `span ${gridFr}`;

      if (keepSsr) return;

      // Get image data
      const imageData = this.getImageData(element, layoutSize, device, isSubGroup);

      // Determine lazy loading and fetchpriority (nur erste Galerie auf der Seite lädt eager -> LCP schnell, Rest lazy)
      const isFirstGallery = element.closest('[data-vc-gallery]') === document.querySelector('[data-vc-gallery]');
      const loading = (isFirstGallery && rowIndex === 0) ? 'eager' : 'lazy';
      const isFirst = isFirstGallery && rowIndex === 0 && itemIndex === 0;

      // Build and inject HTML (pass layoutSize and isSubGroup for srcset sizes calculation)
      element.innerHTML = this.buildHTML(element, imageData, loading, isFirst, layoutSize, isSubGroup);
    }

    /**
     * Get image data based on size and device
     */
    getImageData(element, layoutSize, device, isSubGroup = false) {
      const hasImages = {
        fullsize: element.dataset.hasFullsize === 'true',
        wide: element.dataset.hasWide === 'true',
        narrow: element.dataset.hasNarrow === 'true'
      };

      const images = {
        fullsize: element.dataset.imageFullsize,
        wide: element.dataset.imageWide,
        narrow: element.dataset.imageNarrow
      };

      // Get Shopify image ALT texts (specific to each image size)
      const shopifyAlts = {
        fullsize: element.dataset.altFullsize || '',
        wide: element.dataset.altWide || '',
        narrow: element.dataset.altNarrow || ''
      };

      // Get custom alt text from editor (applies to all image sizes, highest priority)
      const customAltText = element.dataset.altText || '';

      const deviceDimensions = this.dimensionsByDevice[device] || this.dimensionsByDevice.desktop;
      const groupType = isSubGroup ? 'subgroup' : 'group';
      const dimensions = deviceDimensions[groupType];

      // Find first available image
      for (const sizeName of this.fallbackChain[layoutSize]) {
        if (hasImages[sizeName] && images[sizeName]) {
          // Priority: custom alt text > Shopify image alt > empty (will fallback to caption/filename in buildHTML)
          const altText = customAltText || shopifyAlts[sizeName] || '';

          return {
            url: images[sizeName],
            ...dimensions[sizeName],
            imageType: sizeName,  // Track which image type we're using for srcset generation
            alt: altText
          };
        }
      }

      return null;
    }

    /**
     * Build HTML for item
     */
    buildHTML(element, imageData, loading, isFirst = false, layoutSize = 'narrow', isSubGroup = false) {
      const caption = element.dataset.caption || '';
      const link = element.dataset.link || '';

      if (!imageData) {
        return this.buildPlaceholder('NO IMAGE');
      }

      // Alt text priority (SEO-optimized):
      // 1. Custom alt text from editor (imageData.alt, best for SEO)
      // 2. Caption (marketing text, good fallback)
      // 3. Generated from filename (last resort)
      // 4. Generic text (worst case)
      // Images always get alt text for SEO, even if linked
      const imgAlt = imageData.alt || caption || this.generateAltFromFilename(imageData.url) || 'Gallery image';

      const imgTag = this.buildImageTag(imageData, loading, imgAlt, isFirst, layoutSize, isSubGroup);
      const captionTag = caption ? `<p class="vc-gallery-caption">${this.escapeHtml(caption)}</p>` : '';

      if (link) {
        // Accessible link with aria-label (same priority as alt text)
        const ariaLabel = imageData.alt || caption || this.generateAltFromFilename(imageData.url) || 'Produktbild';
        return `<a href="${link}" class="vc-gallery-link" aria-label="${this.escapeHtml(ariaLabel)}">${imgTag}${captionTag}</a>`;
      } else {
        return `<div class="vc-gallery-no-link">${imgTag}${captionTag}</div>`;
      }
    }

    /**
     * Build image tag with srcset
     */
    buildImageTag(imageData, loading, alt, isFirst = false, layoutSize = 'narrow', isSubGroup = false) {
      const baseUrl = imageData.url.replace(/width=\d+/, '');

      // Multi-step srcset like Shopify (10 steps from 0.25x to 2x)
      // Provides optimal image quality at all viewport sizes and devicePixelRatios
      const srcset = this.generateMultiStepSrcset(baseUrl, imageData.width1x, imageData.width2x, imageData.imageType);

      // Sizes attribute based on layout size, device breakpoint, and group type
      // Mobile (<500px): 5px padding each side (10px total)
      // Tablet (500-750px): 5px padding each side (10px total)
      // Desktop (>=751px): 40px padding each side (80px total) until 1520px, then 0px
      let sizes;

      if (isSubGroup) {
        // Subgroup: 50% smaller (8fr, 4fr, 2fr instead of 16fr, 8fr, 4fr)
        if (layoutSize === 'fullsize') {
          // Desktop: 8fr = half width | Tablet: 8fr = half width | Mobile: full width
          sizes = '(min-width: 1520px) 715px, (min-width: 751px) calc((100vw - 80px - 10px) / 2), (min-width: 500px) calc((100vw - 10px - 10px) / 2), calc(100vw - 10px)';
        } else if (layoutSize === 'wide') {
          // Desktop: 4fr = quarter | Tablet: 4fr = quarter | Mobile: full width
          sizes = '(min-width: 1520px) 353px, (min-width: 751px) calc((100vw - 80px - 30px) / 4), (min-width: 500px) calc((100vw - 10px - 10px) / 4), calc(100vw - 10px)';
        } else { // narrow
          // Desktop: 2fr = eighth | Tablet: 2fr = eighth | Mobile: full width
          sizes = '(min-width: 1520px) 171px, (min-width: 751px) calc((100vw - 80px - 70px) / 8), (min-width: 500px) calc((100vw - 10px - 10px) / 8), calc(100vw - 10px)';
        }
      } else {
        // Main Group
        if (layoutSize === 'fullsize') {
          // Desktop: full width | Tablet: full width | Mobile: full width
          sizes = '(min-width: 1520px) 1440px, (min-width: 751px) calc(100vw - 80px), calc(100vw - 10px)';
        } else if (layoutSize === 'wide') {
          // Desktop: 8fr = half | Tablet: full width | Mobile: full width
          sizes = '(min-width: 1520px) 715px, (min-width: 751px) calc((100vw - 80px - 10px) / 2), calc(100vw - 10px)';
        } else { // narrow
          // Desktop: 4fr = quarter | Tablet: 8fr = half | Mobile: full width
          sizes = '(min-width: 1520px) 353px, (min-width: 751px) calc((100vw - 80px - 30px) / 4), (min-width: 500px) calc((100vw - 10px - 10px) / 2), calc(100vw - 10px)';
        }
      }

      const fetchpriority = isFirst ? 'fetchpriority="high"' : '';

      return `<img
        src="${imageData.url}"
        srcset="${srcset}"
        sizes="${sizes}"
        alt="${this.escapeHtml(alt)}"
        loading="${loading}"
        ${fetchpriority}
        class="vc-gallery-image"
        width="${imageData.width1x}"
        height="480"
      >`;
    }

    /**
     * Generate optimized srcset with image-type-specific sizes
     * Prevents upscaling beyond original image dimensions
     * Uses half/quarter sizes for optimal image processing quality
     */
    generateMultiStepSrcset(baseUrl, width1x, width2x, imageType) {
      // Fixed size sets per image type for optimal quality and caching
      // Includes exact halves (353, 715, 1440) for best resampling quality
      // Plus additional intermediate sizes for better viewport coverage
      const sizesByType = {
        narrow: [
          706,   // Original (706×960px)
          500,   // Intermediate
          353,   // Exact half (optimal resampling)
          180    // Quarter range (small displays/subgroups)
        ],
        wide: [
          1430,  // Original (1430×960px)
          1000,  // Intermediate
          715,   // Exact half (optimal resampling)
          500,   // Intermediate
          360,   // Quarter range
          180    // Small displays/subgroups
        ],
        fullsize: [
          2880,  // Original (2880×960px)
          2000,  // Intermediate
          1440,  // Exact half (optimal resampling)
          1000,  // Intermediate
          720,   // Quarter (optimal resampling)
          500,   // Intermediate
          360,   // Small
          180    // Very small/subgroups
        ]
      };

      // Get sizes for this image type
      const sizes = sizesByType[imageType] || sizesByType.fullsize;

      const srcsetParts = sizes.map(width => {
        return `${baseUrl}width=${width} ${width}w`;
      });

      return srcsetParts.join(', ');
    }

    /**
     * Build placeholder for missing image
     */
    buildPlaceholder(text) {
      return `<div class="vc-gallery-placeholder vc-gallery-image">
        ${text}
      </div>`;
    }

    /**
     * Generate alt text from Shopify filename
     * Example: "product-name-color-size_800x.jpg" -> "product name color size"
     */
    generateAltFromFilename(url) {
      try {
        // Extract filename from URL
        const filename = url.split('/').pop().split('?')[0];

        // Remove Shopify version suffix (everything from _ onwards)
        // Example: "image_800x800.jpg" -> "image"
        const nameWithoutVersion = filename.split('_')[0];

        // Remove file extension
        const nameWithoutExt = nameWithoutVersion.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '');

        // Replace hyphens and underscores with spaces
        const readable = nameWithoutExt.replace(/[-_]/g, ' ');

        // Trim and return (empty string if nothing left)
        return readable.trim();
      } catch (error) {
        return '';
      }
    }

    /**
     * Escape HTML to prevent XSS
     */
    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    }
  }

  /**
   * Main Gallery Class
   * Orchestrates the pattern-based gallery rendering
   */
  class VCGallery {
    constructor(galleryElement) {
      this.gallery = galleryElement;
      this.items = Array.from(this.gallery.querySelectorAll('.vc-gallery-item'));
      this.currentDevice = null;

      // Get base URL from data attribute (includes proper CDN path)
      const baseUrl = this.gallery.dataset.patternBaseUrl || '/assets/';

      // Initialize components
      this.patternCache = new PatternCache(baseUrl);
      this.deviceDetector = new DeviceDetector();
      this.patternSelector = new PatternSelector(this.patternCache);
      this.patternApplier = new PatternApplier();
      this.itemRenderer = new ItemRenderer();

      this.init();
    }

    async init() {
      if (this.items.length === 0) return;

      // Set initial device before first render
      this.currentDevice = this.deviceDetector.getCurrentDevice();

      // Initial render
      await this.render();

      // Setup responsive listeners
      this.setupResponsiveListeners();
    }

    setupResponsiveListeners() {
      this.deviceDetector.onChange(async (newDevice) => {
        if (newDevice !== this.currentDevice) {
          this.currentDevice = newDevice;  // Update BEFORE render
          await this.render();
        }
      });

      // Also listen to resize for subgroup height recalculation (debounced)
      let resizeTimeout;
      window.addEventListener('resize', () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = setTimeout(() => {
          const device = this.deviceDetector.getCurrentDevice();
          if (device === 'desktop') {
            const subgroupItems = this.items.filter(item => item.dataset.subGroup === 'true');
            if (subgroupItems.length > 0) {
              this.normalizeSubgroupHeights(subgroupItems);
            }
          }
        }, 150); // Debounce 150ms
      });
    }

    async render() {
      const device = this.deviceDetector.getCurrentDevice();

      // Filter items based on device and show_mobile setting
      let itemsToRender = this.items;

      if (device === 'mobile') {
        // On mobile, filter out items with show_mobile=false
        itemsToRender = this.items.filter(item => {
          const showMobile = item.dataset.showMobile;
          return showMobile === 'true' || showMobile === undefined; // Default to true if not set
        });
      }

      // Hide filtered items on mobile, show all on desktop/tablet
      // Use Set for O(1) lookup instead of O(n) includes
      const renderSet = new Set(itemsToRender);
      this.items.forEach(item => {
        item.style.display = (device === 'mobile' && !renderSet.has(item)) ? 'none' : '';
      });

      // Separate items into groups and subgroups
      const groupItems = [];
      const subgroupItems = [];

      itemsToRender.forEach(item => {
        if (item.dataset.subGroup === 'true') {
          subgroupItems.push(item);
        } else {
          groupItems.push(item);
        }
      });

      // Render group items first
      if (groupItems.length > 0) {
        const groupPattern = await this.patternSelector.selectPattern(
          groupItems.length,
          device,
          false // isSubGroup = false
        );

        if (groupPattern) {
          const groupEnhanced = this.patternApplier.applyPattern(groupItems, groupPattern);
          groupEnhanced.forEach(enhancedItem => {
            this.itemRenderer.render(enhancedItem, device, false); // isSubGroup = false
          });
        }
      }

      // Render subgroup items after
      if (subgroupItems.length > 0) {
        const subgroupPattern = await this.patternSelector.selectPattern(
          subgroupItems.length,
          device,
          true // isSubGroup = true
        );

        if (subgroupPattern) {
          const subgroupEnhanced = this.patternApplier.applyPattern(subgroupItems, subgroupPattern);
          subgroupEnhanced.forEach(enhancedItem => {
            this.itemRenderer.render(enhancedItem, device, true); // isSubGroup = true
          });

          // Normalize subgroup heights after rendering (desktop only)
          if (device === 'desktop') {
            this.normalizeSubgroupHeights(subgroupItems);
          }
        }
      }
    }

    /**
     * Normalize subgroup image heights to avoid row misalignment
     * Calculates average height and applies adjusted aspect-ratios
     * This keeps images responsive while maintaining consistent height
     */
    normalizeSubgroupHeights(subgroupItems) {
      // Wait for next frame to ensure images are rendered
      requestAnimationFrame(() => {
        const images = subgroupItems
          .map(item => item.querySelector('.vc-gallery-image'))
          .filter(img => img !== null);

        if (images.length === 0) return;

        // Calculate actual rendered dimensions
        const dimensions = images.map(img => {
          const rect = img.getBoundingClientRect();
          return {
            img: img,
            width: rect.width,
            height: rect.height
          };
        });

        // Calculate average height
        const avgHeight = dimensions.reduce((sum, d) => sum + d.height, 0) / dimensions.length;

        // Apply new aspect-ratio to each image based on its width and target height
        // This keeps images responsive while normalizing heights
        dimensions.forEach(({ img, width }) => {
          const newAspectRatio = width / avgHeight;
          img.style.aspectRatio = `${newAspectRatio}`;
        });
      });
    }
  }

  /**
   * Initialize galleries on DOM ready
   */
  function init() {
    const galleries = document.querySelectorAll('[data-vc-gallery]');
    galleries.forEach(gallery => new VCGallery(gallery));
  }

  // Wait for DOM
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
